import { jsPDF } from 'jspdf';
import { TRACK_PROFILES, trackStats, fmtDur, WP_KINDS, wpLabel } from './track';
import { clean, mapSnapshot, overviewView } from './exporters';
import { sportStats } from './trackModes';
import { APP_INFO } from './license';
import { tourWeather, weatherText } from './weather';
import { mPerLat, mPerLon } from './geo';

const n1 = (v, d = 1) => (+v).toFixed(d).replace('.', ',');
const hex = (c) => (c || '#16a34a').match(/\w\w/g).map((h) => parseInt(h, 16));

// all photos of a tour: waypoint photos + report photos taken during the tour, by time
export function tourPhotos(tr, project) {
  const wm = Object.fromEntries((project.wpMedia || []).map((m) => [m.id, m.media || []]));
  const out = (tr.wps || []).flatMap((w) => (wm[w.id] || (w.fid && project.fittings.find((f) => f.id === w.fid)?.media) || []).filter((m) => m.kind === 'photo' && m.data).map((m) => ({ ...m, t: m.createdAt || w.t, lat: w.lat, lon: w.lon, alt: w.alt, cap: w.note || '', wp: w })));
  const s = tr.start, e = tr.end || Date.now();
  project.reports.forEach((r) => (r.photos || []).forEach((m) => { if (m.kind === 'photo' && m.data && m.createdAt >= s && m.createdAt <= e) out.push({ ...m, t: m.createdAt, cap: m.caption || '' }); }));
  return out.sort((a, b) => a.t - b.t);
}
// image into a box, kept in proportion and centred
function fit(doc, m, x, y, w, h) {
  const r = m.w && m.h ? m.w / m.h : 4 / 3, bw = Math.min(w, h * r), bh = bw / r;
  doc.addImage(m.data, 'JPEG', x + (w - bw) / 2, y + (h - bh) / 2, bw, bh);
}

// elevation profile page: filled area chart with min / max and distance axis
function elevPage(doc, tr, t, rgb, W, H) {
  const a = (tr.pts || []).filter((p) => p.alt != null);
  if (a.length < 2) return;
  const xs = [0];
  for (let i = 1; i < a.length; i++) xs.push(xs[i - 1] + Math.hypot((a[i].lat - a[i - 1].lat) * mPerLat(a[i].lat), (a[i].lon - a[i - 1].lon) * mPerLon(a[i].lat)));
  const D = xs[xs.length - 1] || 1, mn = Math.min(...a.map((p) => p.alt)), mx = Math.max(...a.map((p) => p.alt)), hh = Math.max(10, mx - mn);
  const X0 = 24, Y0 = 34, CW = W - 48, CH = H - 70, px = (i) => X0 + (xs[i] / D) * CW, py = (v) => Y0 + CH - ((v - mn) / hh) * CH;
  doc.addPage(); doc.setFillColor(248, 245, 239); doc.rect(0, 0, W, H, 'F');
  doc.setTextColor(28, 25, 23); doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.text(clean(t.bookElev), X0, 22);
  doc.setDrawColor(214, 211, 209); doc.setLineWidth(0.2);
  for (let k = 0; k <= 4; k++) { const v = mn + (hh * k) / 4, y = py(v); doc.line(X0, y, X0 + CW, y); doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(120); doc.text(`${Math.round(v)} m`, X0 - 2, y + 1, { align: 'right' }); }
  for (let k = 0; k <= 5; k++) { const x = X0 + (CW * k) / 5; doc.text(`${n1((D * k) / 5000)} km`, x, Y0 + CH + 6, { align: 'center' }); }
  const [r, g, b] = rgb;
  doc.setFillColor(Math.round(r + (255 - r) * 0.7), Math.round(g + (255 - g) * 0.7), Math.round(b + (255 - b) * 0.7));
  for (let i = 1; i < a.length; i++) { const x1 = px(i - 1), x2 = px(i); doc.triangle(x1, Y0 + CH, x1, py(a[i - 1].alt), x2, py(a[i].alt), 'F'); doc.triangle(x1, Y0 + CH, x2, py(a[i].alt), x2, Y0 + CH, 'F'); }
  doc.setDrawColor(r, g, b); doc.setLineWidth(0.9);
  for (let i = 1; i < a.length; i++) doc.line(px(i - 1), py(a[i - 1].alt), px(i), py(a[i].alt));
  const iMax = a.findIndex((p) => p.alt === mx);
  doc.setFillColor(r, g, b); doc.circle(px(iMax), py(mx), 1.6, 'F');
  doc.setTextColor(r, g, b); doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(`${Math.round(mx)} m`, px(iMax), py(mx) - 3, { align: 'center' });
}

// "Erinnerungsbuch": cover, route map with key figures, one big photo per page
export async function tourBook(tracks, project, t, { blob } = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const W = 297, H = 210;
  for (const [ti, tr] of tracks.entries()) {
    const P = TRACK_PROFILES[tr.profile] || { color: '#16a34a' }, s = trackStats(tr), sp = sportStats(tr.profile, s), photos = tourPhotos(tr, tr.__proj || project), [r, g, b] = hex(P.color);
    const wx = weatherText(t, await tourWeather(tr)) || tr.meta?.weather || '';
    if (ti) doc.addPage();
    doc.setFillColor(28, 25, 23); doc.rect(0, 0, W, H, 'F');
    if (photos[0]) fit(doc, photos[0], 0, 0, W, H - 46);
    doc.setFillColor(r, g, b); doc.rect(0, H - 46, W, 46, 'F');
    doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(26);
    doc.text(clean(tr.name || t['trkP_' + tr.profile]), 16, H - 28);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
    doc.text(clean(`${t['trkP_' + tr.profile] || ''} · ${new Date(s.start).toLocaleDateString(t.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`), 16, H - 18);
    doc.text(clean(`${n1(s.km, 2)} km · ${fmtDur(s.ms)} · ↑ ${Math.round(s.up)} m${sp.pace ? ` · ${sp.pace}` : ''}${sp.kcal ? ` · ~${sp.kcal} kcal` : ''} · ${photos.length} ${t.bookPhotos}`), 16, H - 9);
    // map page
    doc.addPage(); doc.setFillColor(248, 245, 239); doc.rect(0, 0, W, H, 'F');
    const pts = (tr.pts || []).map((p) => [p.lat, p.lon]);
    try {
      const img = await mapSnapshot(overviewView(pts), [{ pts, color: P.color }], { noCross: true, markers: (tr.wps || []).map((w, i) => ({ pt: [w.lat, w.lon], n: i + 1, color: WP_KINDS[w.kind]?.color || '#0f172a' })) }, 1000, 640);
      if (img) doc.addImage(img, 'JPEG', 12, 12, 190, 190 * 0.64);
    } catch { /* offline: no map */ }
    const figs = [[t.trkLen, `${n1(s.km, 2)} km`], [t.trkTime, fmtDur(s.ms)], ['Ø', `${n1(s.kmh)} km/h`], [t.trkUp, `${Math.round(s.up)} m`], [t.trkDown, `${Math.round(s.down)} m`], ['max', s.max != null ? `${Math.round(s.max)} m` : '–'], ...(sp.pace ? [[t.stPace, sp.pace]] : []), ...(sp.kcal ? [[t.stKcal, String(sp.kcal)]] : [])];
    figs.forEach(([k, v], i) => {
      const y = 14 + i * 17;
      doc.setFillColor(255, 255, 255); doc.roundedRect(210, y, 75, 14, 3, 3, 'F');
      doc.setTextColor(120); doc.setFontSize(8); doc.text(clean(k), 214, y + 5);
      doc.setTextColor(r, g, b); doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text(clean(v), 214, y + 11.5); doc.setFont('helvetica', 'normal');
    });
    if (wx) {
      const y = 14 + figs.length * 17;
      doc.setFillColor(r, g, b); doc.roundedRect(210, y, 75, 20, 3, 3, 'F');
      doc.setTextColor(255); doc.setFontSize(8); doc.text(clean(t.bookWeather), 214, y + 5);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(doc.splitTextToSize(clean(wx), 67), 214, y + 11); doc.setFont('helvetica', 'normal');
    }
    elevPage(doc, tr, t, [r, g, b], W, H);
    // photo pages
    photos.forEach((m, i) => {
      doc.addPage(); doc.setFillColor(28, 25, 23); doc.rect(0, 0, W, H, 'F');
      fit(doc, m, 8, 8, W - 16, H - 34);
      doc.setTextColor(255); doc.setFontSize(12); doc.setFont('helvetica', 'bold');
      doc.text(clean(`${i + 1} / ${photos.length}  ${m.cap || (m.wp ? wpLabel(t, m.wp) : '')}`), 12, H - 16);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(200);
      doc.text(clean(`${new Date(m.t).toLocaleString(t.locale, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })}${m.lat != null ? ` · ${(+m.lat).toFixed(5)}, ${(+m.lon).toFixed(5)}` : ''}${m.alt != null ? ` · ${Math.round(m.alt)} m` : ''}`), 12, H - 9);
    });
  }
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) { doc.setPage(i); doc.setFontSize(7); doc.setTextColor(160); doc.text(clean(`${APP_INFO.name} · ${i}/${n}`), W - 12, H - 3, { align: 'right' }); }
  if (blob) return doc.output('blob');
  doc.save(`${clean(tracks.length === 1 ? tracks[0].name : project.title || 'Touren').replace(/[^\w-]+/g, '_').slice(0, 40)}_Erinnerungsbuch.pdf`);
  return null;
}
